import { mutate } from 'swr';
import { useMemo, useCallback } from 'react';

import { getEndpoints } from 'src/utils/axios';

import { useWorkspaceContext } from 'src/components/workspace/use-workspace-context';

export const mutatePlaceList = (orgGuid: string) => {
  mutate(
    (key) => typeof key === 'string' && key.startsWith(`/organization/${orgGuid}/places`),
    undefined,
    { revalidate: true }
  );
};

export const mutateStoryList = (orgGuid: string) => {
  mutate(
    (key) => typeof key === 'string' && key.startsWith(`/organization/${orgGuid}/stories`),
    undefined,
    { revalidate: true }
  );
};

export const mutateStoryListByPlace = (placeGuid: string) => {
  mutate(
    (key) => typeof key === 'string' && key.startsWith(`/place/${placeGuid}/stories`),
    undefined,
    { revalidate: true }
  );
};

export const mutateEventList = (orgGuid: string) => {
  mutate(
    (key) => typeof key === 'string' && key.startsWith(`/organization/${orgGuid}/events`),
    undefined,
    { revalidate: true }
  );
};

export const mutateGuideList = (orgGuid: string) => {
  mutate(
    (key) => typeof key === 'string' && key.startsWith(`/organization/${orgGuid}/guides`),
    undefined,
    { revalidate: true }
  );
};

export const mutatePlaceCreationBatches = (orgGuid: string) => {
  mutate(
    (key) => typeof key === 'string' && key.startsWith(`/organization/${orgGuid}/place-creation/batches`),
    undefined,
    { revalidate: true }
  );
};

// Generic mutation for any organization endpoint
export const mutateOrganizationData = (orgGuid: string, endpoint: string) => {
  mutate(
    (key) => typeof key === 'string' && key.startsWith(`/organization/${orgGuid}/${endpoint}`),
    undefined,
    { revalidate: true }
  );
};

export function useRevalidatePlaceImageCache(placeGuid: string) {
  const { orgGuid } = useWorkspaceContext();

  const mutatePlaceCache = useCallback(async () => {
    try {
      // Mutate all relevant place-related endpoints to refresh their data
      await mutate(getEndpoints.place.images(placeGuid));
      mutatePlaceList(orgGuid);
      await mutate(getEndpoints.place.base(placeGuid));

      return true;
    } catch (error) {
      console.error('Error mutating place cache:', error);
      return false;
    }
  }, [placeGuid, orgGuid]);

  const memoizedValue = useMemo(
    () => ({
      mutatePlaceCache,
    }),
    [mutatePlaceCache]
  );

  return memoizedValue;
}

export function mutatePublicPlaceCache() {
  mutate(
    (key) => typeof key === 'string' && key.startsWith(`/public/place`),
    undefined,
    { revalidate: true }
  );
}